import {
  ContentDescriptor,
  ContentRating,
  EndUserCohort,
  EpisodesSortOrder,
  PassportLevel,
  PlatformType,
  PosterSize,
  ShowType,
  UserRole,
  VideoType,
} from '@api/db/enums'

import { AudiostreamProfile } from './jsonEnums.js'

class EnumLabelMap<E extends string | number> {
  private toLabel: Record<E, string>
  private toEnum: Record<string, E>

  constructor(mapping: Record<E, string>) {
    this.toLabel = mapping
    this.toEnum = {} as Record<string, E>
    for (const key in mapping) {
      const label = mapping[key]
      this.toEnum[label] = key as unknown as E
    }
  }

  getLabel(enumValue: E): string | undefined {
    return this.toLabel?.[enumValue]
  }

  getEnum(label: string): E | undefined {
    return this.toEnum[label]
  }

  *[Symbol.iterator](): Iterator<[E, string]> {
    for (const [key, value] of Object.entries(this.toLabel)) {
      yield [key as unknown as E, value as string]
    }
  }
}

export const AudiostreamProfileLabels = new EnumLabelMap<AudiostreamProfile>({
  [AudiostreamProfile.AAC]: 'ICY / direct AAC',
  [AudiostreamProfile.HLS_AAC]: 'HLS (AAC) - required',
  [AudiostreamProfile.HLS_MP3]: 'HLS (MP3)',
  [AudiostreamProfile.MP3]: 'ICY / direct MP3',
})

export const ContentDescriptorLabels = new EnumLabelMap<ContentDescriptor>({
  [ContentDescriptor.D]: 'D - Sexual or suggestive dialogue',
  [ContentDescriptor.FV]: 'FV - Fantasy violence',
  [ContentDescriptor.L]: 'L - Coarse or crude language',
  [ContentDescriptor.S]: 'S - Sexual situations',
  [ContentDescriptor.V]: 'V - Violence',
})

export const ContentRatingLabels = new EnumLabelMap<ContentRating>({
  [ContentRating.TVY]: 'TV-Y',
  [ContentRating.TVY7]: 'TV-Y7',
  [ContentRating.TV14]: 'TV-14',
  [ContentRating.TVG]: 'TV-G',
  [ContentRating.TVPG]: 'TV-PG',
  [ContentRating.TVMA]: 'TV-MA',
  [ContentRating.NR]: 'NA',
})

export const EndUserCohortLabels = new EnumLabelMap<EndUserCohort>({
  [EndUserCohort.MEMBER]: 'Member',
  [EndUserCohort.PROSPECT]: 'Prospect',
})

export const EpisodeSortOrderLabels = new EnumLabelMap<EpisodesSortOrder>({
  [EpisodesSortOrder.EARLIEST]: 'Earliest',
  [EpisodesSortOrder.EARLIEST_FROM_LATEST_SEASON]:
    'Earliest from Latest season',
  [EpisodesSortOrder.LATEST]: 'Latest',
})

export const PassportLevelLabels = new EnumLabelMap<PassportLevel>({
  [PassportLevel.ALL]: 'All Passport',
  [PassportLevel.NONE]: 'No Passport',
  [PassportLevel.SOME]: 'Some Passport',
})

export const PlatformTypeLabels = new EnumLabelMap<PlatformType>({
  [PlatformType.MOBILE]: 'Mobile',
  [PlatformType.OTT]: 'OTT',
  [PlatformType.WEB]: 'Web',
})

export const PosterSizeLabels = new EnumLabelMap<PosterSize>({
  [PosterSize.PORTRAIT_MEDIUM]: 'Portrait (Medium)',
  [PosterSize.PORTRAIT_SMALL]: 'Portrait (Small)',
  [PosterSize.LANDSCAPE_MEDIUM]: 'Landscape (Medium)',
  [PosterSize.LANDSCAPE_SMALL]: 'Landscape (Small)',
})

export const UserRoleLabels = new EnumLabelMap<UserRole>({
  [UserRole.ADMIN]: 'Administrator',
  [UserRole.GLOBAL_GM]: 'Global GM',
  [UserRole.GLOBAL_MARKETER]: 'Global Marketer',
  [UserRole.GLOBAL_PROGRAMMER]: 'Global Programmer',
  [UserRole.STATION_GM]: 'Station GM',
  [UserRole.STATION_MARKETER]: 'Station Marketer',
  [UserRole.STATION_PROGRAMMER]: 'Station Programmer',
})

export const ShowTypeLabels = new EnumLabelMap<ShowType>({
  [ShowType.EPISODIC_SERIES]: 'Episodic series',
  [ShowType.MOVIE]: 'Movie',
  [ShowType.NEWS]: 'News',
  [ShowType.NON_EPISODIC_SERIES]: 'Non-episodic series',
})

export const VideoTypeLabels = new EnumLabelMap<VideoType>({
  [VideoType.CLIP]: 'Clip',
  [VideoType.EPISODE]: 'Episode',
  [VideoType.PREVIEW]: 'Preview',
  [VideoType.SPECIAL]: 'Special',
})
